import {Component, OnInit, OnDestroy, Input} from '@angular/core';
import {trackEntityById} from 'projects/core/src/public-api';
import {WsService} from '../../../../data/ws.service';
import {TasksService} from 'projects/desktop/src/app/modules/home-page/data/tasks/tasks.service';
import {Task} from 'projects/desktop/src/app/modules/home-page/data/tasks/task';
import {
  TaskEditDialogComponent
} from 'projects/desktop/src/app/modules/home-page/view/home-board/dash-board-main-block/task-column/task-edit-dialog/task-edit-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {ShortTask} from 'projects/desktop/src/app/modules/home-page/data/tasks/short-task';
import {WsUpdateResponce} from 'projects/app-misc/src/app/modules/data/ws-update-responce';
import {debounceTime, filter} from 'rxjs/operators';
import {Subscription, forkJoin} from 'rxjs';

interface TaskTab {
  title: string;
  tasks: ShortTask[];
  completed: number;
  total: number;
  loading: boolean;
}

@Component({
  selector: 'app-tasks-block',
  templateUrl: './tasks-block.component.html',
  styleUrls: ['./tasks-block.component.scss']
})
export class TasksBlockComponent implements OnInit, OnDestroy {

  @Input()
  projectId: number;

  @Input()
  projectName: string;

  tabs: TaskTab[] = [
    { title: 'Regular', tasks: [], completed: 0, total: 0, loading: false },
    { title: 'Work Orders', tasks: [], completed: 0, total: 0, loading: false },
    { title: 'Checkitems & Comments', tasks: [], completed: 0, total: 0, loading: false }
  ];

  selectedTabIndex = 0;

  trackEntityById = trackEntityById;

  wsSub: Subscription;

  constructor(
    protected dialog: MatDialog,
    protected tasksService: TasksService,
    protected wsService: WsService
  ) {
  }

  ngOnInit() {
    this.loadAllTabs();
    this.wsSubToTasks();
  }

  wsSubToTasks() {
    this.wsSub = this.wsService
      .listenUntilDestroyed<WsUpdateResponce<Task>>('/topic/update/model.task.task', this)
      .pipe(
        filter(updateInfo => updateInfo.entity.project?.name === this.projectName),
        debounceTime(2000)
      )
      .subscribe(() => {
        this.loadAllTabs();
      })
  }

  loadAllTabs() {
    this.tabs.forEach(tab => tab.loading = true);

    forkJoin({
      regularTasks: this.tasksService.getProjectTabsRegular(this.projectId),
      workOrderTasks: this.tasksService.getProjectTabsWorkOrder(this.projectId),
      checkItemTasks: this.tasksService.getProjectTabsCheckItem(this.projectId),
      counts: this.tasksService.getProjectTabsCount(this.projectId)
    }).subscribe(result => {
      this.tabs[0].tasks = this.mapTasks(result.regularTasks);
      this.tabs[0].completed = result.counts.taskCompleted || 0;
      this.tabs[0].total = (result.counts.taskCompleted || 0) + (result.counts.taskIncomplete || 0);
      this.tabs[0].loading = false;

      this.tabs[1].tasks = this.mapTasks(result.workOrderTasks);
      this.tabs[1].completed = result.counts.workOrderCompleted || 0;
      this.tabs[1].total = (result.counts.workOrderCompleted || 0) + (result.counts.workOrderIncomplete || 0);
      this.tabs[1].loading = false;

      this.tabs[2].tasks = this.mapTasks(result.checkItemTasks);
      this.tabs[2].completed = result.counts.checkitemCommentCompleted || 0;
      this.tabs[2].total = (result.counts.checkitemCommentCompleted || 0) + (result.counts.checkitemCommentIncomplete || 0);
      this.tabs[2].loading = false;

      this.selectFirstNonEmptyTab();
    });
  }

  selectFirstNonEmptyTab() {
    const firstNonEmpty = this.tabs.findIndex(tab => tab.tasks.length > 0);
    this.selectedTabIndex = firstNonEmpty >= 0 ? firstNonEmpty : 0;
  }

  mapTasks(tasks: Task[]): ShortTask[] {
    return tasks.map(task => {
      const tempTask = {
        id: task.id,
        assignedUserName: task.assignedUser?.name,
        authorName: task.author?.name,
        completed: task.completed,
        text: task.text,
        clazz: task.clazz,
        totalComments: task.totalComments,
        projectId: task.project ? task.project.id : null,
        projectName: task.project ? task.project.name : null,
        checkitemsCompleted: task.checkitemsCompleted,
        checkitemsTotal: task.checkitemsTotal,
        deadline: task.deadline,
        photoIds: task.photoIds,
        hasFiles: task.hasFiles,
        number: task.number,
        isWorkOnSite: task.isWorkOnSite
      } as ShortTask;
      return tempTask;
    });
  }

  editTask(task: Task) {
    const dialogRef = this.dialog.open(TaskEditDialogComponent, {
      data: {
        task,
        buttonLabel: 'Save'
      },
      panelClass: 'task-edit-dialog',
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'DELETE') {
        this.tabs.forEach(tab => {
          const index = tab.tasks.indexOf(task, 0);
          if (index > -1) {
            tab.tasks.splice(index, 1);
          }
        });
        this.loadAllTabs();
      } else {
        const tempTask = result as Task;
        if (tempTask) {
          this.tabs.forEach(tab => {
            const ftask = tab.tasks.find(ttask => ttask.id === tempTask.id);
            if (ftask) {
              ftask.text = tempTask.text;
              ftask.assignedUserName = tempTask.assignedUser?.name;
              ftask.projectId = tempTask.project ? tempTask.project.id : null;
              ftask.projectName = tempTask.project ? tempTask.project.name : null;
              ftask.deadline = tempTask.deadline;
            }
          });
          this.loadAllTabs();
        }
      }
    });
  }

  returnZero() {
    return 0;
  }

  createTask() {
    const dialogRef = this.dialog.open(TaskEditDialogComponent, {
      data: {
        task: null,
        buttonLabel: 'Create',
        projectId: this.projectId,
        projectName: this.projectName
      },
      panelClass: 'task-edit-dialog',
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && (result as Task).project && (result as Task).project.id === this.projectId) {
        this.loadAllTabs();
      }
    });
  }

  ngOnDestroy() {
    if (this.wsSub) {
      this.wsSub.unsubscribe();
      this.wsSub = null;
    }
  }

}
